import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack, useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { View } from "react-native";
import { Badge } from "~/components/feedback/Badge";
import { DeleteMedicalButton } from "~/components/form/health-profile/DeleteMedicalButton";
import { HeaderButton } from "~/components/HeaderButton";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { Page } from "~/components/layout/Page";
import { Subtitle, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

const type = MedicalType.condition;

export default function ConditionDetails() {
    const { id }: { id: string } = useLocalSearchParams();
    const { findMedical } = useMedical();
    const { findOption } = useOptions();
    const condition = useMemo(() => findMedical(type, id), [id, findMedical]);

    if (!condition) {
        return <Dismiss />;
    }

    const option = findOption(OptionType.conditions, condition.option_id);

    return (
        <Authorize ability="view" section="conditions">
            <Stack.Screen
                options={{
                    title: "Condition details",
                    headerRight: () => (
                        <Can ability="update" section="conditions">
                            <HeaderButton
                                title="Edit"
                                href={{ pathname: "/health-profile/conditions/[id]/edit", params: { id } }}
                            />
                        </Can>
                    ),
                }}
            />
            <Page spacer>
                <View style={[Padding.top_md, Gap.lg, Flex.flex]}>
                    <View style={[Items.start, Gap.xs]}>
                        <Only when={condition.primary}>
                            <Badge title="Primary condition" variant="info" size="sm" />
                        </Only>
                        <Only when={option}>
                            <Title size="xl">{option?.title}</Title>
                        </Only>
                        <Only when={condition.confirmed}>
                            <Subtitle>Known condition</Subtitle>
                        </Only>
                    </View>
                    <DataList>
                        <DataListItem title="Description" data={condition.description} />
                    </DataList>
                    <BasicGallery images={condition.photograph_ids} />
                </View>
                <DeleteMedicalButton type={type} id={id} />
            </Page>
        </Authorize>
    );
}
